import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import Button from '../Button';
import ProfilePhoto from '../ProfilePhoto';
import SearchResult from '../SearchResult/SearchResult';
import Divider from '../Divider/Divider';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  MODAL_MODES,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
} from '../../reducers/favoriteSlice';
import { deleteFollowListBySlug, setFollowListBySlug } from '../../api/api';
import { getImageUrl } from '../../utils';
import Close from '../../../../consumer_web/app/modules/svg/dynamic/close.svg';
import Checkmark from '../../svg/icons/checkmark.svg';
import Plus from '../../svg/icons/plus.svg';

import './NotifyChefsListModal.scss';
import Icon from '../Icon';
import { CHEF_LIST } from '../../utils/getPageContext';

const NotifyChefsListModal = ({
  Analytics,
  isFollowed,
  isMultiStepModalOpen,
  listSlug,
  onClose,
  notifyVenueName,
  notifyChefName,
  notifyChefTitle,
  notifyChefImage,
  chefList,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const [isFollowing, setIsFollowing] = useState(isFollowed);

  const handleCloseModal = () => {
    onClose();
  };

  const handleFavoriteClick = (response) => {
    const { venue_id: venueId, venue } = response;

    dispatch(setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.locality, context: 'Chef recommendation' }));
    dispatch(setMultiStepModalVenueId(venueId));
    dispatch(setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST));
    dispatch(setMultiStepModalOpenState(true));
  };

  const toggleFollowList = () => {
    if (isFollowing) {
      deleteFollowListBySlug(listSlug);
      setIsFollowing(false);
    } else {
      setFollowListBySlug(listSlug);
      setIsFollowing(true);
    }
  };

  const getPrimaryCTAText = () => (
    <span className="NotifyChefsListModal__cta">
      {isFollowing ? (
        <>
          <Icon name="checkmark" component={Checkmark} />
          {getTranslation({ translationKey: 'following_list', fallback: 'Following' })}
        </>
      ) : (
        <>
          <Icon name="plus" component={Plus} />
          {getTranslation({ translationKey: 'follow_list', fallback: 'Follow List' })}
        </>
      )}
    </span>
  );

  return (
    <Modal
      isOpen={isOpen && !isMultiStepModalOpen}
      onRequestClose={handleCloseModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      hasFooter
      hasSecondaryCTA={false}
      primaryCTAText={getPrimaryCTAText()}
      primaryCTAVariant="primary"
      primaryCTAAdditionalProps={{
        onClick: toggleFollowList,
        'data-test-id': 'notify-chefs-list-primary-cta',
      }}
      styles={{
        content: {
          maxWidth: '400px',
          inset: '15vh 0 auto',
        },
      }}
    >
      <div className="NotifyChefsListModal">
        <div className="NotifyChefsListModal__header">
          <Button
            onClick={handleCloseModal}
            variant="no-style"
            className="Button__close"
            aria-label={getTranslation({
              translationKey: 'close',
              fallback: 'Close',
            })}
            data-test-id="notify-chefs-list-button-close"
          >
            <Close />
          </Button>
        </div>

        <div className="NotifyChefsListModal__title-container">
          <TranslationProvider translationKey="notify_table_chefs_list_title" translationValues={{ notifyVenueName }}>
            <div className="NotifyChefsListModal__title">
              {`When you didn’t get off the Notify list for ${notifyVenueName}...`}
            </div>
          </TranslationProvider>
        </div>

        <div className="NotifyChefsListModal__chef-container">
          <div className="NotifyChefsListModal__chef-container__picture">
            <ProfilePhoto
              isGda={false}
              size="40"
              url={notifyChefImage || undefined}
            />
          </div>

          {notifyChefName && (
            <div className="NotifyChefsListModal__subtitle">{notifyChefName}</div>
          )}

          {notifyChefTitle ? (
            <div className="NotifyChefsListModal__sec-info">{notifyChefTitle}</div>
          ) : (
            <TranslationProvider translationKey="same_group_recommendations_eyebrow" translationValues={{ name: notifyVenueName }}>
              <div className="NotifyChefsListModal__sec-info">
                From the
                {' '}
                {notifyVenueName}
                {' '}
                team
              </div>
            </TranslationProvider>
          )}
        </div>

        <hr className="NotifyChefsListModal__hr" />

        <div className="NotifyChefsListModal__card-container">
          {chefList?.map((venue, index) => (
            <div key={venue.id.resy}>
              <SearchResult
                venue={{
                  ...venue,
                  cuisine: venue.type,
                  currency: venue.currency_symbol,
                  id: venue.id.resy,
                  image: getImageUrl(venue.responsive_images),
                  priceRange: venue.price_range_id,
                  urlSlug: venue.url_slug,
                }}
                canFavorite
                context={CHEF_LIST}
                showBadges={false}
                showSlots={false}
                onFavoriteClick={(favorite) => handleFavoriteClick(favorite)}
                isMobile
                event={Analytics.event}
                compact
                showBorder={false}
              />
              {index + 1 < chefList.length && (
                <Divider marginHorizontal={10} />
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export const NotifyChefsListModalPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  isFollowed: PropTypes.bool.isRequired,
  isMultiStepModalOpen: PropTypes.bool.isRequired,
  listSlug: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  notifyVenueName: PropTypes.string.isRequired,
  notifyChefName: PropTypes.string,
  notifyChefTitle: PropTypes.string,
  notifyChefImage: PropTypes.string,
  isOpen: PropTypes.bool,
  chefList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

NotifyChefsListModal.propTypes = NotifyChefsListModalPropTypes;

NotifyChefsListModal.defaultProps = {
  notifyChefName: undefined,
  notifyChefTitle: undefined,
  notifyChefImage: undefined,
  isOpen: false,
};

const mapStateToProps = ({ favorite }) => ({
  isMultiStepModalOpen: favorite.isMultiStepModalOpen,
});

NotifyChefsListModal.displayName = 'NotifyChefsListModal';
export default connect(mapStateToProps)(NotifyChefsListModal);
